import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

// Componentes específicos para el detalle del modelo v2
import BreadcrumbsModelDetail from "../components/BreadcrumbsModelDetail";
import CarGallery from "../components/ModelDetailNew/CarGallery";
import ColorSelector from "../components/ModelDetailNew/ColorSelector";
import PaymentOptions from "../components/ModelDetailNew/PaymentOptions";
import PurchaseTimeframeSelector from "../components/ModelDetailNew/PurchaseTimeframe";
import ItemsSelector from "../components/ModelDetailNew/ItemsSelector";
import VersionPriceAndBonus from "../components/ModelDetailNew/VersionPriceAndBonus";
import VersionSelector from "../components/ModelDetailNew/VersionSelector";
import FixedSummary from "../components/ModelDetailNew/FixedSummary";
import Stepper from "../components/ModelDetailNew/Stepper";

// Modales de interacción
import ModalContact from "../components/Modals/ModalContactMe";
import ModalPersonalDataNew from "../components/Modals/ModalPersonalDataNew";

// Hooks para gestión de datos y lógica de negocio
import useConfiguratorData from "../hooks/useConfiguratorData";
import useLeadData from "../hooks/useLeadData";
import usePaymentOptions from "../hooks/usePaymentOptions";

/**
 * Componente principal para el detalle de modelo v2
 * Gestiona la configuración completa de un vehículo incluyendo:
 * - Selección de versión y colores
 * - Opciones de pago (Contado, MAF, Kinto)
 * - Simulaciones de financiamiento
 * - Captura de datos personales y leads
 * - Control de inactividad del usuario
 */
const ModelDetailNew = () => {
    // Hooks para gestión de datos del configurador
    const {
        getSelectedModel,
        getSelectedVersion,
        getLoading,
        getVersionAndModelByVersionId,
        setSelectedModel,
        setSelectedVersion,
        setPreselectedDealerId,
    } = useConfiguratorData();

    const { getLeadId, updateLead } = useLeadData();
    const { isMAF, isKinto } = usePaymentOptions();

    // Estados locales del componente
    const selectedModel = getSelectedModel();
    const selectedVersion = getSelectedVersion();
    const loading = getLoading();
    const { version_id } = useParams();
    const history = useHistory();
    const queryParams = new URLSearchParams(location.search);
    const clientData = {
        first_name: queryParams.get("first_name") || "",
        last_name: queryParams.get("last_name") || "",
        rut: queryParams.get("rut") || "",
        phone_number: queryParams.get("phone_number") || "",
        email: queryParams.get("email") || "",
        company_name: queryParams.get("company_name") || "",
    };
    const preselectedDealershipId = queryParams.get("dealership_id") || null;

    // Estados para control de modales
    const [isOpen, setIsOpen] = useState(true);
    const [modalContactIsOpen, setModalContactIsOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(1);
    const [personalDataCompleted, setPersonalDataCompleted] = useState(false);

    // Referencias para las secciones del configurador
    const configurationRef = useRef(null);
    const paymentRef = useRef(null);

    // Configura dealership preseleccionado desde URL
    useEffect(() => {
        const query = new URLSearchParams(location.search).get("did");
        if (query) {
            setPreselectedDealerId(parseInt(query));
        }
    }, []);

    // Inicializa la página con el modelo/versión desde URL
    const initPage = () => {
        if (version_id && !loading) {
            const { model, version } = getVersionAndModelByVersionId(parseInt(version_id));
            // Si no se encuentra la versión o tiene advertencia de stock, redirigir a home
            if (!version || version.stock_warning) {
                history.replace("/");
                return;
            }

            setSelectedModel(model);
            setSelectedVersion(version);
        }
    };

    // Actualiza lead cuando cambia la versión
    useEffect(() => {
        if (getLeadId() && selectedModel && selectedVersion) {
            updateLead();
        }

        // Reiniciar scroll al cambiar la versión
        if (selectedVersion) {
            window.scrollTo(0, 0);
        }
    }, [selectedVersion]);

    useEffect(() => {
        initPage();
    }, [loading, version_id]);

    // Efecto para detectar el scroll y actualizar el step activo
    useEffect(() => {
        const handleScroll = () => {
            if (!configurationRef.current || !paymentRef.current) return;

            // Usar un offset más pequeño para que se active más abajo
            const scrollPosition = window.scrollY + window.innerHeight / 3;

            const configurationTop = configurationRef.current.offsetTop;
            const paymentTop = paymentRef.current.offsetTop;

            if (scrollPosition >= paymentTop) {
                setActiveStep(2);
            } else if (scrollPosition >= configurationTop) {
                setActiveStep(1);
            }
        };

        window.addEventListener("scroll", handleScroll);
        // Ejecutar una vez al montar para establecer el step inicial
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return (
        <>
            {!loading && selectedVersion ? (
                <>
                    <ModalContact
                        isOpen={modalContactIsOpen}
                        onCloseModal={() => setModalContactIsOpen(false)}
                    />
                    <BreadcrumbsModelDetail empty />
                    <ModalPersonalDataNew
                        isOpen={isOpen}
                        onCloseModal={() => {
                            setIsOpen(false);
                            window.scrollTo(0, 0);
                        }}
                        onComplete={() => {
                            setPersonalDataCompleted(true);
                        }}
                        selectedModel={selectedModel}
                        selectedVersion={selectedVersion}
                        clientData={clientData}
                        preselectedDealershipId={preselectedDealershipId}
                    />

                    <div className="model-detail">
                        <div className="main-container model-detail-container">
                            <div className="left-section">
                                <CarGallery />
                            </div>
                            <div className="right-section">
                                <Stepper activeStep={activeStep} />
                                <div ref={configurationRef}>
                                    <VersionPriceAndBonus
                                        model={selectedModel}
                                        version={selectedVersion}
                                    />
                                    <VersionSelector />
                                    <ColorSelector />
                                </div>
                                <div ref={paymentRef}>
                                    <PaymentOptions
                                        personalDataCompleted={personalDataCompleted}
                                    />
                                    <PurchaseTimeframeSelector />
                                    {/* El resumen vive ahora en la barra fija; aquí solo
                                        quedan los selectores de accesorios, mantenciones y usado */}
                                    {!isKinto && <ItemsSelector isMAF={isMAF} />}
                                </div>
                            </div>
                        </div>
                    </div>

                    <FixedSummary onQuoteClick={() => setModalContactIsOpen(true)} />
                </>
            ) : (
                <div className="spinner-container">
                    <div className="spinner"></div>
                </div>
            )}
        </>
    );
};

export default ModelDetailNew;
